"use client";

import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Spinner } from "@/components/ui/spinner";
import { UI } from "@/constants";
import { SearchParams } from "@/types";
import { Search, X } from "lucide-react";
import { memo } from "react";
import { useProductsSearch } from "./useProductsSearch";

export const ProductsSearch = memo((params: SearchParams) => {
  const {
    open,
    handleProductClick,
    value,
    setValue,
    hasProducts,
    showNotFound,
    state,
    isPending,
    isQueryPending,
    formRef,
    action,
  } = useProductsSearch(params);

  return (
    <Popover open={open}>
      <PopoverTrigger asChild>
        <form ref={formRef} action={action}>
          <InputGroup className="h-8 max-w-46">
            <InputGroupAddon>
              {isPending || isQueryPending ? <Spinner /> : <Search />}
            </InputGroupAddon>
            <InputGroupInput
              name="name"
              placeholder={isQueryPending ? UI.info.loading : UI.placeholders.search}
              value={value}
              onChange={e => setValue(e.target.value)}
              autoComplete="off"
            />
            {value && (
              <InputGroupAddon align="inline-end">
                <InputGroupButton onClick={() => setValue("")}>
                  <X />
                </InputGroupButton>
              </InputGroupAddon>
            )}
          </InputGroup>
        </form>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="flex flex-col gap-2 max-w-46"
        onOpenAutoFocus={e => e.preventDefault()}
        onCloseAutoFocus={e => e.preventDefault()}
      >
        {isPending && <p className="text-sm text-muted-foreground">{UI.info.searching}</p>}
        {hasProducts && (
          <ul className="flex flex-col gap-2">
            {state.products.map(product => (
              <li
                key={product.productId}
                onClick={() => handleProductClick(product.productId)}
                className="text-sm text-muted-foreground cursor-pointer hover:font-semibold truncate"
              >
                {product.productName}
              </li>
            ))}
          </ul>
        )}
        {showNotFound && <p className="text-sm text-muted-foreground">{UI.info.noProductsFound}</p>}
      </PopoverContent>
    </Popover>
  );
});

ProductsSearch.displayName = "ProductsSearch";
